import React, { useLayoutEffect, useRef, useState } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";
import { RecentSearchItem } from "apis/loadsServices";
import { History, ChevronDown } from "lucide-react";
import RecentRoutIcon from "assets/images/RecentRoutIcon.svg"

interface RecentSearchesDropdownProps {
  label?: string;
  items: RecentSearchItem[];
  isLoading: boolean;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  onSelect: (item: RecentSearchItem) => void;
  renderItem: (item: RecentSearchItem) => React.ReactNode;
  onOpen?: () => void;
  buttonClassName?: string;
}

const RecentSearchesDropdown: React.FC<RecentSearchesDropdownProps> = ({
  label = "Recent Searches",
  items,
  isLoading,
  isOpen,
  setOpen,
  onSelect,
  renderItem,
  onOpen,
  buttonClassName,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  useClickOutside(containerRef, () => setOpen(false));

  useLayoutEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const container = containerRef.current;
      const btn = buttonRef.current;
      const menu = menuRef.current;
      if (!container || !btn || !menu) return;

      const containerRect = container.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const margin = 16;
      const menuWidth = menu.offsetWidth;

      const idealViewportLeft = btnRect.left + btnRect.width / 2 - menuWidth / 2;
      const maxViewportLeft = window.innerWidth - menuWidth - margin;
      const viewportLeft = Math.max(margin, Math.min(idealViewportLeft, maxViewportLeft));

      setMenuStyle({
        position: "absolute",
        // Matches the ~6px gap the other dropdowns here (Nearby Airports,
        // Stops) use via their mt-1.5.
        top: btnRect.bottom - containerRect.top + 6,
        left: viewportLeft - containerRect.left,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    const next = !isOpen;
    setOpen(next);
    if (next) {
      onOpen?.();
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        className={
          buttonClassName ||
          "flex items-center gap-1.5 px-3 h-[34px] bg-white border border-[#E5E7EB] rounded-lg hover:bg-gray-50 transition-colors text-xs font-semibold font-manrope text-[#374151] shadow-sm"
        }
        onClick={handleToggle}
      >
        {/* <History className="w-3.5 h-3.5 text-[#4B5563]" /
        > */}
        <img className="w-4" src={RecentRoutIcon} alt="airline logo" />
        <span>{label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          style={menuStyle}
          className="w-max min-w-[12rem] max-w-[calc(100vw-2rem)] max-h-80 overflow-hidden bg-white rounded-2xl shadow-xl border border-gray-100 z-50 flex flex-col p-1"
        >
          <ul className="overflow-auto divide-y divide-gray-100">
            {isLoading ? (
              <li className="px-4 py-3 text-xs text-gray-400 text-center">
                Loading…
              </li>
            ) : items.length === 0 ? (
              <li className="px-4 py-3 text-xs text-gray-400 text-center">
                No recent searches
              </li>
            ) : (
              items.slice(0, 5).map((item, idx) => (
                <li
                  key={item.id ?? idx}
                  onClick={() => {
                    onSelect(item);
                    setOpen(false);
                  }}
                  className="px-3.5 py-2.5 text-sm cursor-pointer text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {renderItem(item)}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RecentSearchesDropdown;
